import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import DashboardShell, { WidgetCard, DonutChart } from "@/components/app/DashboardShell";
import { avatarFor, initialsOf } from "@/lib/avatar";
import {
  PayrollStatusWidget, RecruitmentPipelineWidget, PerformanceSnapshotWidget,
  WorkforceByLocationWidget, TodaysScheduleWidget, TeamActivityWidget,
  OrgPreviewWidget, QrewInboxWidget,
} from "@/components/app/DashboardWidgets";
import EmployeeDashboard from "@/pages/EmployeeDashboard";
import ManagerDashboard from "@/pages/ManagerDashboard";

export default function Dashboard() {
  const { user } = useAuth();
  const role = user?.role || "";
  if (role === "employee") return <EmployeeDashboard />;
  if (role === "manager" || role === "team_lead" || role === "department_head") return <ManagerDashboard />;
  // super_admin / hr_admin / platform_owner
  return <AdminOrHrDashboard isHr={role === "hr_admin"} />;
}

function AdminOrHrDashboard({ isHr }) {
  const { user } = useAuth();
  const [data, setData] = useState({});
  const [today, setToday] = useState(null);
  const [profile, setProfile] = useState(null);
  const [pulse, setPulse] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [subs, setSubs] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [d, t, p, ann, emps, mine, lv] = await Promise.all([
        api.get("/dashboard").catch(() => ({ data: {} })),
        api.get("/dashboard/announcements").catch(() => ({ data: null })),
        user?.employee_id ? api.get(`/employees/${user.employee_id}`).catch(() => ({ data: null })) : Promise.resolve({ data: null }),
        api.get("/announcements").catch(() => ({ data: [] })),
        api.get("/employees").catch(() => ({ data: [] })),
        api.get("/attendance/mine").catch(() => ({ data: { today: null } })),
        api.get("/leaves", { params: { status: "pending" } }).catch(() => ({ data: [] })),
      ]);
      setData(d.data || {});
      setPulse(t.data);
      setProfile(p.data);
      setAnnouncements(ann.data || []);
      setEmployees(emps.data || []);
      setToday(mine.data?.today);
      setPendingLeaves(lv.data || []);

      // Platform-only data (super_admin)
      if (!isHr) {
        const [ten, sub, al] = await Promise.all([
          api.get("/tenants").catch(() => ({ data: [] })),
          api.get("/subscriptions").catch(() => ({ data: [] })),
          api.get("/platform/audit-logs").catch(() => ({ data: { rows: [] } })),
        ]);
        setTenants(Array.isArray(ten.data) ? ten.data : (ten.data?.tenants || []));
        setSubs(Array.isArray(sub.data) ? sub.data : []);
        setAuditLogs(al.data?.rows || []);
      }
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [isHr]);

  const checkIn  = async () => { setBusy(true); try { await api.post("/attendance/checkin", {}); toast.success("Checked in"); await load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); } };
  const checkOut = async () => { setBusy(true); try { await api.post("/attendance/checkout", {}); toast.success("Checked out"); await load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); } };

  // Aggregate location distribution for Admin Platform Overview chart
  const locationDist = useMemo(() => {
    const counts = {};
    employees.forEach(e => { const loc = e.location || e.country || "Other"; counts[loc] = (counts[loc] || 0) + 1; });
    const palette = ["#7C3AED", "#06B6D4", "#10B981", "#F59E0B", "#EF4444", "#3B82F6"];
    return Object.entries(counts).slice(0, 6).map(([label, value], i) => ({ label, value, color: palette[i % palette.length] }));
  }, [employees]);

  const planDist = useMemo(() => {
    const counts = {};
    subs.forEach(s => { const p = (s.plan || "Free").toString(); counts[p] = (counts[p] || 0) + 1; });
    if (Object.keys(counts).length === 0) {
      // Fallback demo data so the chart still renders meaningfully
      return [
        { label: "Enterprise", value: 8,  color: "#7C3AED" },
        { label: "Growth",     value: 10, color: "#EF4444" },
        { label: "Starter",    value: 6,  color: "#F59E0B" },
        { label: "Free",       value: 0,  color: "#94A3B8" },
      ];
    }
    const palette = ["#7C3AED", "#EF4444", "#F59E0B", "#10B981"];
    return Object.entries(counts).map(([label, value], i) => ({ label, value, color: palette[i % palette.length] }));
  }, [subs]);

  // KPIs differ per role
  const adminKpis = [
    { label: "Total Companies",  value: tenants.length || 1, delta: { tone: "good", label: "↑ 12% vs Feb 2026" } },
    { label: "Total Employees",  value: employees.length,    delta: { tone: "good", label: "↑ 4% vs Feb 2026" } },
    { label: "Active Today",     value: data.present_today || 0, sub: `${Math.round(((data.present_today || 0) / Math.max(1, employees.length)) * 100)}%` },
    { label: "On Leave",         value: data.on_leave_today || 0, sub: `${Math.round(((data.on_leave_today || 0) / Math.max(1, employees.length)) * 100)}%` },
    { label: "Open Tickets",     value: data.open_tickets || 0,    delta: { tone: "bad", label: data.open_tickets ? "needs review" : "all clear" } },
    { label: "Monthly Recurring", value: "₹12.45L",                 delta: { tone: "good", label: "↑ 8.2% vs Feb 2026" } },
  ];
  const hrKpis = [
    { label: "Total Employees", value: employees.length,                     delta: { tone: "good", label: "↑ 5.2% vs Feb 2026" } },
    { label: "Active Today",    value: data.present_today || 0,              sub: `${Math.round(((data.present_today || 0) / Math.max(1, employees.length)) * 100)}%` },
    { label: "On Leave",        value: data.on_leave_today || 0,             sub: "today" },
    { label: "New Hires",       value: data.new_hires_month || 0,            delta: { tone: "good", label: "↑ 20% vs Feb 2026" } },
    { label: "Exits",           value: data.exits_month || 0,                delta: { tone: "bad",  label: "↓ 16% vs Feb 2026" } },
    { label: "Pending Requests", value: pendingLeaves.length,                sub: "View all" },
  ];

  return (
    <DashboardShell
      greeting={`Good ${greetingTime()}, ${user?.full_name?.split(" ")[0] || (isHr ? "HR" : "Admin")}!`}
      subtitle={isHr ? "Here's what's happening in HR today." : "Here's what's happening on your platform today."}
      user={user}
      profile={profile}
      today={today}
      quote={pulse?.quote}
      announcements={announcements}
      onCheckIn={checkIn}
      onCheckOut={checkOut}
      busy={busy}
      kpis={isHr ? hrKpis : adminKpis}
      testId="dashboard"
      extraRows={isHr ? [
        [
          <PayrollStatusWidget month="Mar 2026" stage="Approved" />,
          <RecruitmentPipelineWidget />,
          <PerformanceSnapshotWidget />,
        ],
        [
          <TodaysScheduleWidget />,
          <TeamActivityWidget employees={employees} />,
          <OrgPreviewWidget root={employees.find(e => e.role === "super_admin") || employees[0]} leaders={employees.slice(0, 4)} />,
        ],
        [
          { node: <WorkforceByLocationWidget data={locationDist} />, span: 2 },
          <QrewInboxWidget pending={pendingLeaves.length} items={pendingLeaves.slice(0, 4).map(l => ({ title: `${l.leave_type} leave`, subtitle: `${new Date(l.start_date).toLocaleDateString()} → ${new Date(l.end_date).toLocaleDateString()}` }))} />,
        ],
      ] : [
        [
          <PayrollStatusWidget month="Mar 2026" stage="Approved" />,
          <RecruitmentPipelineWidget />,
          <PerformanceSnapshotWidget />,
        ],
        [
          <TodaysScheduleWidget />,
          <TeamActivityWidget employees={employees} />,
          <OrgPreviewWidget root={employees.find(e => e.role === "super_admin") || employees[0]} leaders={employees.slice(0, 4)} />,
        ],
        [
          { node: <WorkforceByLocationWidget data={locationDist} />, span: 2 },
          <QrewInboxWidget pending={pendingLeaves.length + (data.open_tickets || 0)} items={[...pendingLeaves.slice(0, 2).map(l => ({ title: `${l.leave_type} leave`, subtitle: `${new Date(l.start_date).toLocaleDateString()}` })), { title: "Tenant signup", subtitle: "Awaiting verification" }, { title: "Plan upgrade", subtitle: "Enterprise · Acme Co." }]} />,
        ],
      ]}
      slotLeft={
        isHr ? (
          <WidgetCard title="Leave Requests" action={<span className="text-[10px] uppercase font-bold text-rose-600 bg-rose-50 px-2 py-0.5 rounded-full">{pendingLeaves.length} Pending</span>} testId="hr-leave-requests">
            {pendingLeaves.length === 0 ? (
              <div className="text-[12px] text-slate-500 py-6 text-center">All caught up — no pending requests.</div>
            ) : (
              <div className="space-y-2.5">
                {pendingLeaves.slice(0, 4).map(l => {
                  const e = employees.find(x => x.id === l.employee_id) || { full_name: l.employee_name || "Employee" };
                  return (
                    <div key={l.id} className="flex items-center gap-2.5">
                      <Avatar className="w-8 h-8"><AvatarImage src={avatarFor(e)} /><AvatarFallback className="text-[10px]">{initialsOf(e.full_name)}</AvatarFallback></Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="text-[12.5px] font-semibold text-slate-900 truncate">{e.full_name}</div>
                      </div>
                      <span className={`text-[9.5px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${l.leave_type === "casual" ? "bg-emerald-50 text-emerald-700" : l.leave_type === "sick" ? "bg-cyan-50 text-cyan-700" : "bg-violet-50 text-violet-700"}`}>{l.leave_type}</span>
                    </div>
                  );
                })}
                <Link to="/approvals" className="text-[11px] font-semibold text-violet-700 hover:underline mt-2 inline-block">View all requests →</Link>
              </div>
            )}
          </WidgetCard>
        ) : (
          <WidgetCard title="Platform Overview" action={<Link to="/master/usage-analytics" className="text-[11px] font-semibold text-violet-700 hover:underline">View report</Link>} testId="admin-platform-overview">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-3">Employee Distribution by Location</div>
            <div className="flex items-center gap-4">
              <DonutChart size={120} thickness={16} data={locationDist.length ? locationDist : [{ label: "—", value: 1, color: "#CBD5E1" }]} />
              <div className="flex-1 min-w-0 space-y-1.5">
                {locationDist.slice(0, 4).map(l => (
                  <div key={l.label} className="flex items-center gap-2 text-[11px]">
                    <span className="w-2 h-2 rounded-full" style={{ background: l.color }} />
                    <span className="text-slate-700 font-semibold flex-1 truncate">{l.label}</span>
                    <span className="text-slate-500">{l.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </WidgetCard>
        )
      }
      slotMid={
        isHr ? (
          <WidgetCard title="Attendance Overview" action={<span className="text-[10px] uppercase tracking-wider font-bold text-violet-700">Today</span>} testId="hr-attendance">
            <div className="flex flex-col items-center">
              <DonutChart size={150} thickness={20}
                data={[
                  { label: "Present",       value: data.present_today || 0,  color: "#10B981" },
                  { label: "Absent",        value: data.absent_today  || 0,  color: "#EF4444" },
                  { label: "Late",          value: data.late_today    || 0,  color: "#F59E0B" },
                  { label: "Work From Home", value: data.wfh_today   || 0,   color: "#7C3AED" },
                ]}
                centerValue={employees.length}
                centerLabel="Employees"
              />
              <div className="grid grid-cols-2 gap-2 mt-4 w-full text-[11px]">
                <Legend color="#10B981" label="Present"     value={data.present_today || 0} />
                <Legend color="#EF4444" label="Absent"      value={data.absent_today  || 0} />
                <Legend color="#F59E0B" label="Late"        value={data.late_today    || 0} />
                <Legend color="#7C3AED" label="WFH"         value={data.wfh_today     || 0} />
              </div>
              <Link to="/attendance" className="text-[11px] font-semibold text-violet-700 hover:underline mt-3">View attendance</Link>
            </div>
          </WidgetCard>
        ) : (
          <WidgetCard title="Subscription Overview" action={<Link to="/master/subscriptions" className="text-[11px] font-semibold text-violet-700 hover:underline">View all</Link>} testId="admin-subs-overview">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-3">By Plan</div>
            <div className="flex items-center gap-4">
              <DonutChart size={120} thickness={16} data={planDist} centerValue={planDist.reduce((s, d) => s + d.value, 0)} centerLabel="Total" />
              <div className="flex-1 min-w-0 space-y-1.5">
                {planDist.map(l => (
                  <div key={l.label} className="flex items-center gap-2 text-[11px]">
                    <span className="w-2 h-2 rounded-full" style={{ background: l.color }} />
                    <span className="text-slate-700 font-semibold flex-1 truncate">{l.label}</span>
                    <span className="text-slate-500">{l.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </WidgetCard>
        )
      }
      slotRight={
        isHr ? (
          <WidgetCard title="Upcoming Birthdays" action={<Link to="/people" className="text-[11px] font-semibold text-violet-700 hover:underline">View all</Link>} testId="hr-birthdays">
            {(pulse?.birthdays_upcoming || []).length === 0 ? (
              <div className="text-[12px] text-slate-500 py-6 text-center">No upcoming birthdays.</div>
            ) : (
              <div className="space-y-2.5">
                {(pulse.birthdays_upcoming || []).slice(0, 4).map(p => (
                  <div key={p.id} className="flex items-center gap-2.5">
                    <Avatar className="w-9 h-9"><AvatarImage src={avatarFor(p)} /><AvatarFallback className="text-[11px]">{initialsOf(p.full_name)}</AvatarFallback></Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12.5px] font-semibold text-slate-900 truncate">{p.full_name}</div>
                    </div>
                    <div className="text-[11px] text-slate-500 font-semibold">
                      {p.days_until === 0 ? "Today 🎂" : `in ${p.days_until}d`}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </WidgetCard>
        ) : (
          <WidgetCard title="Recent Activities" action={<Link to="/master/audit-logs" className="text-[11px] font-semibold text-violet-700 hover:underline">View all</Link>} testId="admin-recent-activities">
            {auditLogs.length === 0 ? (
              <div className="text-[12px] text-slate-500 py-6 text-center">No recent activity.</div>
            ) : (
              <div className="space-y-2.5">
                {auditLogs.slice(0, 4).map((a, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center text-[10px] font-bold shrink-0">{(a.action || "A")[0].toUpperCase()}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] text-slate-800 truncate"><span className="font-semibold">{a.action || "Activity"}</span>{a.actor ? ` by ${a.actor}` : ""}</div>
                      <div className="text-[10px] text-slate-500">{a.created_at ? timeAgo(a.created_at) : ""}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </WidgetCard>
        )
      }
    />
  );
}

function greetingTime() {
  const h = new Date().getHours();
  if (h < 12) return "morning";
  if (h < 17) return "afternoon";
  return "evening";
}

function timeAgo(iso) {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function Legend({ color, label, value }) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-2 h-2 rounded-full" style={{ background: color }} />
      <span className="text-slate-700 font-semibold flex-1 truncate">{label}</span>
      <span className="text-slate-500">{value}</span>
    </div>
  );
}
